import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  InputAdornment,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";

// Supabase
import supabase from "../../supabaseClient";

// i18n
import { useTranslation } from "react-i18next";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "80%", md: "60%", lg: "40%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  overflowY: "auto",
  p: { xs: 3, md: 5 },
};

export default function Group({ open, onClose, group }) {
  const [contacts, setContacts] = useState([]);
  const [initialSelectedContacts, setInitialSelectedContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const groupNameRef = useRef(null);
  const groupDescriptionRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const filteredContacts = contacts.filter((contact) =>
    `${contact.first_name ?? ""} ${contact.last_name ?? ""} ${contact.countrycode_id
      }`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Context
  const { teamId } = useAuth();
  const { showNotification } = useNotification();

  // i18n
  const { t } = useTranslation();

  useEffect(() => {
    const fetchContacts = async () => {
      if (group && contacts.length === 0) {
        let allContacts = [];
        let offset = 0;
        const pageSize = 1000;

        while (true) {
          const { data: contactsData, error: contactsError } = await supabase
            .from("contacts")
            .select("*")
            .order("first_name", { ascending: true })
            .range(offset, offset + pageSize - 1);

          if (contactsError) {
            showNotification(
              `${t("group.errorFetchingContacts")}: ${contactsError.message}`,
              "error"
            );
            break;
          }

          if (contactsData && contactsData.length > 0) {
            allContacts = [...allContacts, ...contactsData];
          }

          if (!contactsData || contactsData.length < pageSize) {
            break;
          }

          offset += pageSize;
        }

        const contactsData = allContacts;
          const selected = contactsData.filter(
            (contact) =>
              contact.groups_ids && contact.groups_ids.includes(group.id)
          );
          const unselected = contactsData.filter(
            (contact) =>
              !contact.groups_ids || !contact.groups_ids.includes(group.id)
          );

        // Merge selected at the top and unselected at the bottom
        setContacts([...selected, ...unselected]);
        const groupContactsIds = selected.map((contact) => contact.id);

        setSelectedContacts(groupContactsIds);
        setInitialSelectedContacts(groupContactsIds);
      }
    };

    fetchContacts();
  }, [group]);

  const handleContactChange = (contactId) => {
    trackEvent("group-group_change");
    setSelectedContacts((prevSelected) => {
      if (prevSelected.includes(contactId)) {
        return prevSelected.filter((id) => id !== contactId);
      } else {
        return [...prevSelected, contactId];
      }
    });
  };

  const handleSubmit = async () => {
    trackEvent("group-form_submit");
    try {
      const groupName = groupNameRef.current.value;
      const groupDescription = groupDescriptionRef.current.value;
      if (!group.id) {
        const { data, error } = await supabase
          .from("groups")
          .insert([
            {
              name: groupName,
              description: groupDescription,
              team_id: teamId,
            },
          ])
          .select();

        if (error) {
          throw error;
        }

        group.id = data[0].id; // Assign the ID of the new group
      } else {
        const { error: groupError } = await supabase
          .from("groups")
          .update({ name: groupName, description: groupDescription })
          .eq("id", group.id);

        if (groupError) {
          throw groupError;
        }
      }

      // Find added contacts
      selectedContacts.forEach(async (id) => {
        if (!initialSelectedContacts.includes(id)) {
          // Add group to contact
          const contact = contacts.find((contact) => contact.id === id);
          const updatedGroupsIds = Array.isArray(contact.groups_ids)
            ? [...contact.groups_ids, group.id]
            : [group.id];

          const { error } = await supabase
            .from("contacts")
            .update({ groups_ids: updatedGroupsIds })
            .eq("id", id);

          if (error) {
            throw error;
          }
        }
      });

      // Find removed contacts
      initialSelectedContacts.forEach(async (id) => {
        if (!selectedContacts.includes(id)) {
          // Remove group from contact
          const contact = contacts.find((contact) => contact.id === id);
          const updatedGroupsIds = contact.groups_ids.filter(
            (id) => id !== group.id
          );

          const { error } = await supabase
            .from("contacts")
            .update({ groups_ids: updatedGroupsIds })
            .eq("id", id);

          if (error) {
            throw error;
          }
        }
      });

      showNotification(t("group.success"), "success");
      resetValues();
    } catch (error) {
      showNotification(`${t("group.error")}: ${error.message}`, "error");
    }
  };

  const handleDelete = async () => {
    try {
      if (group) {
        for (const contact of contacts) {
          if (contact.groups_ids && contact.groups_ids.includes(group.id)) {
            showNotification(t("group.deleteError"), "error");
            return;
          }
        }

        const { error: deleteError } = await supabase
          .from("groups")
          .delete()
          .eq("id", group.id);

        if (deleteError) {
          throw deleteError;
        }
        trackEvent("group-delete");
        showNotification(t("group.deleteSuccess"), "success");
        resetValues();
      }
    } catch (error) {
      showNotification(
        `${t("group.errorDeletingGroup")}: ${error.message}`,
        "error"
      );
    }
  };

  const resetValues = () => {
    setContacts([]);
    setInitialSelectedContacts([]);
    setSelectedContacts([]);
    setSearchTerm("");

    onClose();
  };

  return (
    <Modal open={open} onClose={resetValues}>
      <Box sx={style}>
        {group && (
          <>
            <TextField
              fullWidth
              inputRef={groupNameRef}
              label={t("group.name")}
              defaultValue={group.name || ""}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              inputRef={groupDescriptionRef}
              label={t("group.description")}
              defaultValue={group.description || ""}
              sx={{ mb: 2 }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <TextField
                value={searchTerm}
                placeholder={t("group.searchContacts")}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "50%",
                  borderRadius: "8px",
                }}
              />
              <Typography variant="body1" sx={{ ml: 2 }}>
                {t("group.selectedCount", { count: selectedContacts.length })}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "8px",
                  mb: 2,
                }}
              >
                <List>
                  {filteredContacts.map((contact) => (
                    <ListItem key={contact.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedContacts.includes(contact.id)}
                            onChange={() => handleContactChange(contact.id)}
                          />
                        }
                        label={`${contact.first_name ?? ""} ${contact.last_name ?? ""
                          } (${contact.countrycode_id})`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box sx={{ display: "flex", mt: 4, gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  fullWidth
                  sx={{
                    borderRadius: "8px",
                    fontWeight: "bold",
                    py: 1.5,
                    background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                    "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                  }}
                >
                  {group.id ? t("group.saveChanges") : t("group.addGroup")}
                </Button>

                {group.id && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                    fullWidth
                    sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" }, color: "#e2726e", borderColor: "#e2726e", py: 1.5 }}
                  >
                    {t("group.deleteGroup")}
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}
